import {useTranslation} from "react-i18next";
import {useEffect, useRef, useState} from "react";
import {Button, Form, Input, Select} from "antd";
import DatePicker from "../../core/components/DatePicker";
import ApiService from "../../core/service/api-service";
import RequestTypeSelectBox from "../request-type/RequestTypeSelectBox";
import DeviceSelectBox from "../device/DeviceSelectBox";
import {useHistory} from "react-router-dom";
import {Pages} from "../../AppRouter";
import moment from "moment";
import ModalitySelectBox from "../modality/ModalitySelectBox";
import HospitalSelectBox from "../hospital/HospitalSelectBox";

export default function WorklistItemCreateEdit({onSaved})
{
    const {t} = useTranslation();
    const history = useHistory();
    const formRef = useRef();
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [selectedHospitalId, setSelectedHospitalId] = useState(null);
    const [selectedModalityId, setSelectedModalityId] = useState(null);
    const [selectedRequestType, setSelectedRequestType] = useState(null);
    const [hospitals, setHospitals] = useState([]);
    

    const save = async (form) => {
        let response;
        const data = {
            ...form,
            hospitalId: selectedHospitalId,
            modalityId: selectedModalityId,
            dateOfBirth: form.dateOfBirth ? form.dateOfBirth.toISOString() : null,
        };

        response = await ApiService.WorklistItem.Create(data);

        if(response.success)
        {
            if(onSaved)
            {
                onSaved(response.data);
            }
            else
            {
                history.push(Pages.WorklistItem.DataGrid);
            }
        }
    }
    

    const handleRequestTypeChange = async (requestTypeId) => {
        if(requestTypeId) {
            const requestTypeResponse = await ApiService.RequestType.GetById(requestTypeId);
            if(requestTypeResponse.success) {
                setSelectedRequestType(requestTypeResponse.data);
                // Auto-fill examDescription and studyDescription with Request Type Display Name
                formRef.current.setFieldsValue({
                    examDescription: requestTypeResponse.data.displayName || '',
                    studyDescription: requestTypeResponse.data.displayName || ''
                });
            }
        } else {
            setSelectedRequestType(null);
            formRef.current.setFieldsValue({
                examDescription: '',
                studyDescription: ''
            });
        }
    }

    return (
        <Form
            ref={formRef}
            onFinish={save}
            layout={"vertical"}
        >
            <Form.Item
                name={'hospitalId'}
                label={t('Hospital')}
                rules={[{required: true, message: t('Please select hospital')}]}
            >
                <HospitalSelectBox 
                    mode="single"
                    value={selectedHospitalId}
                    onChange={(hospitalId) => setSelectedHospitalId(hospitalId)}
                    onData={(hospitals) => {
                        setHospitals(hospitals);
                        setSelectedHospitalId(hospitals?.[0]?.id);
                    }}
                />
            </Form.Item>

            <Form.Item
                name={"modalityId"}
                label={t('Modality')}
                rules={[{required: true, message: t('Please select modality')}]}
            >
                <ModalitySelectBox 
                    mode="single"
                    onChange={(modalityId) => {
                        setSelectedModalityId(modalityId);
                        formRef.current.setFieldsValue({ requestTypeId: null });
                        setSelectedRequestType(null);
                    }} 
                    value={selectedModalityId}
                />
            </Form.Item>

            <Form.Item
                name={"patientId"}
                label={t('Patient ID')}
                rules={[{required: true, message: t('Please enter patient ID')}]}
            >
                <Input />
            </Form.Item>

            <Form.Item
                name={"patientName"}
                label={t('Patient Name')}
                rules={[{ required: true, message: t('Please enter patient name') }]}
            >
                <Input />
            </Form.Item>

            <Form.Item
                name={"patientLastname"}
                label={t('Patient Lastname')}
                rules={[{required: true, message: t('Please enter patient lastname')}]}
            >
                <Input />
            </Form.Item>

            <Form.Item
                name={"sex"}
                label={t('Sex')}
                rules={[{required: true, message: t('Please select sex')}]}
            >
                <Select>
                    <Select.Option value="M">{t('Male')}</Select.Option>
                    <Select.Option value="F">{t('Female')}</Select.Option>
                    <Select.Option value="O">{t('Other')}</Select.Option>
                </Select>
            </Form.Item>

            <Form.Item
                name={"dateOfBirth"}
                label={t('Date of Birth')}
                rules={[{required: true, message: t('Please select date of birth')}]}
            >
                <DatePicker 
                    style={{width: '100%'}} 
                    format="YYYY-MM-DD" 
                    placeholder="1996-03-16"
                    allowClear
                />
            </Form.Item>

            <Form.Item
                name={"requestTypeId"}
                label={t('Request Type')}
                rules={[{ required: true, message: t('Please select request type') }]}
            >
                <RequestTypeSelectBox modalityId={selectedModalityId} onChange={handleRequestTypeChange}  />
            </Form.Item>

            <Form.Item
                name={"accessionNumber"}
                label={t('Accession Number')}
                rules={[{ required: true, message: t('Please enter accession number') }]}
            >
                <Input />
            </Form.Item>

            <Form.Item
                name={"studyDescription"}
                label={t('Study Description')}
                tooltip={t('Auto-filled from Request Type Display Name')}
            >
                <Input.TextArea rows={3} disabled={!!selectedRequestType} />
            </Form.Item>

            <Form.Item>
                <Button type={"primary"} htmlType={"submit"} style={{marginRight: 8}}>
                    {t('Create')}
                </Button>
                <Button onClick={() => onSaved ? onSaved() : history.push(Pages.WorklistItem.DataGrid)}>
                    {t('Cancel')}
                </Button>
            </Form.Item>
        </Form>
    )
}
